import { Settings } from 'luxon';

import { ENV } from '@/core/config';
import { LanguageStorage } from '@/core/StorageManager';
import { getSynapseLocale, setSynapseLocale } from '@/core/SynapsePreferences';
import { LanguageOption } from '@/core/types';

function getLocaleData(locale) {
  return import(`../../locales/${locale}.json`);
}

/**
 * Returns the current user locale.
 * Priority: 1. Application-selected language, 2. Browser language, 3. Default 'en'.
 */
export const getUserLocale = (): string => {
  return (
    LanguageUtilsService.getCurrentLanguage()?.code ||
    navigator.language ||
    'en'
  );
};

export const RTL_LANGUAGES = ['fa', 'ar', 'he', 'ur'];

export const isRTL = (code?: string): boolean => {
  const currentCode =
    code || LanguageUtilsService.getCurrentLanguage()?.code || 'en';
  return RTL_LANGUAGES.includes(currentCode);
};

class LanguageUtilsServiceClass {
  currentLanguage: LanguageOption;

  dictionary: Record<string, string> = {};

  getCurrentLanguage() {
    return this.currentLanguage;
  }

  setCurrentLanguage(language: LanguageOption) {
    if (!language) return;

    this.currentLanguage = language;

    // Keep Waldur's native storage for backward compatibility and API headers.
    LanguageStorage.set(language.code);

    // Keep the shared Synapse preference in sync across applications.
    if (language.code === 'fa' || language.code === 'en') {
      setSynapseLocale(language.code);
    }
    getLocaleData(language.code)
      .then((mod) => {
        this.dictionary = mod.default;
      })
      .catch(() => {
        // Stored code no longer matches a locale file (e.g. removed from
        // server config); checkLanguage() will replace it with a valid
        // choice once server config is available.
      });
    Settings.defaultLocale = language.code;

    const dir = isRTL(language.code) ? 'rtl' : 'ltr';
    document.documentElement.setAttribute('dir', dir);
    document.documentElement.setAttribute('direction', dir);
    document.documentElement.setAttribute('lang', language.code);
    if (isRTL(language.code)) {
      document.documentElement.classList.add('rtl-mode');
    } else {
      document.documentElement.classList.remove('rtl-mode');
    }
  }

  /**
   * Best-effort language priming for the pre-config splash screen.
   *
   * `checkLanguage()` needs `ENV.languageChoices`/`ENV.defaultLanguage`,
   * which only exist after the server config request in `loadConfig()`
   * resolves — so translated strings rendered by `LoadingScreen` while that
   * request is in flight always fall back to English. This starts loading
   * the dictionary for the user's last explicitly-selected language (a code
   * already validated against real locale files when it was stored) as soon
   * as the script runs, in parallel with the config request, instead of
   * waiting for it. `checkLanguage()` still runs afterwards and is
   * authoritative — this only shortens the initial English flash.
   */

  primeLanguage() {
    const urlLang = new URLSearchParams(window.location.search).get('language');
    const lang = urlLang || getSynapseLocale() || LanguageStorage.get();

    if (lang) {
      this.setCurrentLanguage({ code: lang, label: lang });
    }
  }

  checkLanguage() {
    // Check if current language is listed in choices and
    // switch to default language if current choice is invalid.
    // Fallback to first option in languageChoices list if defaultLanguage is invalid.
    // Priority: URL param > Synapse cookie > localStorage > defaultLanguage > first choice
    const urlParams = new URLSearchParams(window.location.search);
    const urlLang = urlParams.get('language');
    const cookieLang = getSynapseLocale();
    const storedLang = LanguageStorage.get();

    const current =
      this.findLanguageByCode(urlLang) ||
      this.findLanguageByCode(cookieLang) ||
      this.findLanguageByCode(storedLang) ||
      this.findLanguageByCode(ENV.defaultLanguage) ||
      this.getChoices()[0];

    this.setCurrentLanguage(current);
  }

  findLanguageByCode(code?: string) {
    if (!code) {
      return;
    }
    return this.getChoices().filter((language) => language.code === code)[0];
  }

  getChoices() {
    return ENV.languageChoices;
  }
}

export const LanguageUtilsService = new LanguageUtilsServiceClass();
